import { z } from "zod";

/**
 * Shift status filter values for the guided support flow.
 * Used to filter shifts by their current status.
 */
export const ShiftStatusFilter = {
  UPCOMING: "upcoming",
  PAST: "past",
} as const;

export type ShiftStatusFilterType = (typeof ShiftStatusFilter)[keyof typeof ShiftStatusFilter];

/**
 * Shift completion status for display in the support flow.
 * Only includes active shifts where the worker remains assigned.
 * Cancelled/deleted shifts are filtered out and not displayed.
 */
export const ShiftCompletionStatus = {
  COMPLETED: "completed",
  UPCOMING: "upcoming",
} as const;

export type ShiftCompletionStatusType =
  (typeof ShiftCompletionStatus)[keyof typeof ShiftCompletionStatus];

/**
 * Payment status for completed shifts.
 */
export const PaymentStatus = {
  PAID: "paid",
  PENDING: "pending",
  PAYMENT_ISSUE: "payment_issue",
} as const;

export type PaymentStatusType = (typeof PaymentStatus)[keyof typeof PaymentStatus];

/**
 * Schema for a shift displayed in the interactive support shift selection list.
 * Only includes active shifts where the worker remains assigned.
 *
 * Money values are stored in cents to avoid floating-point precision issues.
 * Time values are stored in hours as decimals for calculations.
 * Formatting to display strings happens in the UI layer only.
 */
export const supportShiftSchema = z.object({
  id: z.string(),
  workplaceName: z.string(),
  startAt: z.string(),
  endAt: z.string(),
  timezone: z.string(),
  status: z.enum(["completed", "upcoming"]),
  /** Hours worked as a decimal number (e.g., 8.5 for 8h 30m). Formatting happens in UI. */
  hoursWorkedInHours: z.number().optional(),
  /** Payment amount in cents to avoid float math issues. Formatting happens in UI. */
  paymentAmountInCents: z.number().optional(),
  paymentStatus: z.enum(["paid", "pending", "payment_issue"]).optional(),
});

export type SupportShift = z.infer<typeof supportShiftSchema>;

/**
 * Props for the shift card component in the support flow.
 */
export interface SupportShiftCardProps {
  shift: SupportShift;
  onSelect: (shiftId: string) => void;
}

/**
 * Schema for the guided support page route params.
 */
export const guidedSupportRouteParamsSchema = z.object({});

export type GuidedSupportRouteParams = z.infer<typeof guidedSupportRouteParamsSchema>;

/**
 * Schema for the shift details page route params.
 */
export const shiftDetailsRouteParamsSchema = z.object({
  shiftId: z.string(),
});

export type ShiftDetailsRouteParams = z.infer<typeof shiftDetailsRouteParamsSchema>;
